import { Box, Center, Collapse, Fade, Flex, HStack, IconButton, Link, Spacer, Spinner, Stack, StyleProps } from '@chakra-ui/react';
import { createContext, FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { create } from 'zustand';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';

import { Button } from '../../../design-system/button';
import { ArrowLeftOutline, CheckSolid, ChevronUpOutline, ThumbDownOutline, ThumbUpOutline } from '../../../design-system/icon';
import { Heading, Text } from '../../../design-system/typography';
import { rem } from '../../../design-system/utils';
import { useCurrentCompany } from '../../../lib/who-am-i';
import { DimensionKey, useQualitySuggestions } from '../../../queries/recognition-analysis';
import { Reveal } from '../../display/reveal';
import { DraftAssistCard } from './components/draft-assist-card';
import { CupFiller } from './cup-filler';
import { DraftAssistProp } from './hooks/use-draft-assist';
import { SlidingText } from './sliding-text';
import { calculatePercentageStop } from './utils';

type Stage = 'checklist' | 'to-detail' | 'detail' | 'to-checklist';

type DimensionContextValue = {
	stage: Stage;
};

export const DimensionContext = createContext<DimensionContextValue>({ stage: 'checklist' });

const TRANSITION_TIME_IN_MS = 200;

export const useScoreboxState = () => {
	const [stage, setStage] = useState<Stage>('checklist');
	const [dimension, setDimension] = useState<Dimension | null>(null);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const onViewDimension = (dimension: Dimension) => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}

		setDimension(dimension);
		setStage('to-detail');

		timeoutRef.current = setTimeout(() => setStage('detail'), TRANSITION_TIME_IN_MS);
	};

	const onCloseDimension = () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}

		setStage('to-checklist');

		timeoutRef.current = setTimeout(() => {
			setStage('checklist');
			setDimension(null);
		}, TRANSITION_TIME_IN_MS);
	};

	useEffect(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
	}, []);

	return {
		dimension,
		isViewing: stage === 'to-detail' || stage === 'detail',
		isTransitioning: stage === 'to-detail' || stage === 'to-checklist',
		onCloseDimension,
		onViewDimension,
		stage,
	};
};

export const useDimensionAccordion = (overrideIndex?: number | null) => {
	// null = user hasn't interacted yet; a number = user explicitly opened/closed an item.
	const [active, setActive] = useState<number | null>(null);

	const register = useCallback(
		(index: number) => {
			// Once the user has interacted (active is non-null), respect their choice.
			// Only fall back to overrideIndex as the initial default when the user hasn't
			// toggled anything yet — this auto-expands the target dimension on suggestion
			// arrival without locking the accordion against further user interaction.
			const effectiveActive = active !== null ? active : (overrideIndex ?? null);
			return {
				isOpen: effectiveActive === index,
				onToggle: () => setActive(active === index ? null : index),
			};
		},
		[active, overrideIndex]
	);

	return { register };
};

export type Dimension = {
	key: DimensionKey;
	reason: string;
	matches: boolean;
};

type DimensionBoxProps = {
	dimension: Dimension;
	isOpen: boolean;
	/** When provided, renders a "view examples" link inside the expanded dimension. Omit to hide the link. */
	onClick?: () => void;
	onToggle: () => void;
	text: string;
	unmetBgColor?: string;
	suggestion?: string;
	isLoadingSuggestion?: boolean;
	onAddSuggestion?: (text: string) => void;
	onRegenerateSuggestion?: () => void;
};

export const DimensionBox: FC<DimensionBoxProps> = ({
	dimension,
	isOpen,
	onClick,
	onToggle,
	text,
	unmetBgColor = 'bg.subtle',
	suggestion,
	isLoadingSuggestion = false,
	onAddSuggestion,
	onRegenerateSuggestion,
}) => {
	const { t } = useTranslation('recognition', { keyPrefix: 'gratitudeGauge' });
	const hasCoreValues = useCurrentCompany().core_values.length > 0;
	const { track } = useAnalytics();

	return (
		<Box bgColor={dimension.matches ? 'hashtag.bgAlpha' : unmetBgColor} p={2} borderRadius="12px" data-testid={`dimension-box-${dimension.key}`}>
			<HStack
				as="button"
				columnGap={1.5}
				onClick={() => {
					track(AnalyticsEvent.GratitudeGauge.DimensionDetailClicked, { dimension: dimension.key, matches: dimension.matches });
					onToggle();
				}}
				w="full"
			>
				<DimensionCheckbox isChecked={dimension.matches} />
				<Text size="sm" variant="strong" color="text.default">
					{t('title', { context: dimension.key })}
				</Text>
				<Spacer />
				<ChevronUpOutline size="sm" transitionProperty="transform" transitionDuration="fast" transform={isOpen ? 'none' : 'rotate(180deg)'} />
			</HStack>

			<Collapse in={isOpen} animateOpacity>
				<Text my={4} size="sm" color="text.default">
					{dimension.reason || t('placeholder', { context: dimension.key })}
				</Text>

				{isLoadingSuggestion && !suggestion && (
					<Center py={2} mb={2}>
						<Spinner size="sm" color="purple.300" />
					</Center>
				)}

				{suggestion && onAddSuggestion && onRegenerateSuggestion && (
					<DraftAssistCard
						suggestion={suggestion}
						dimensionKey={dimension.key}
						isLoading={false}
						onAdd={() => onAddSuggestion(suggestion)}
						onRegenerate={onRegenerateSuggestion}
					/>
				)}

				<Reveal if={!!onClick && !!text}>
					<Link
						as="button"
						fontSize="sm"
						color="text.interactive"
						onClick={() => {
							track(AnalyticsEvent.GratitudeGauge.ViewDimensionExamples, { dimension: dimension.key });
							onClick?.();
						}}
					>
						{dimension.key === DimensionKey.ValuesAligned && hasCoreValues ? t('valuesLink') : t('exampleLink')}
					</Link>
				</Reveal>
			</Collapse>
		</Box>
	);
};

type Particle = {
	x: number;
	y: number;
	vx: number;
	vy: number;
	life: number;
	maxLife: number;
};

const createCheckboxBurst = (checkboxElement: HTMLElement) => {
	const canvas = document.createElement('canvas');
	canvas.width = window.innerWidth;
	canvas.height = window.innerHeight;
	canvas.style.position = 'fixed';
	canvas.style.left = '0px';
	canvas.style.top = '0px';
	canvas.style.pointerEvents = 'none';
	canvas.style.zIndex = '9999';

	document.body.appendChild(canvas);

	const ctx = canvas.getContext('2d');
	if (!ctx) {
		document.body.removeChild(canvas);
		return;
	}

	const rect = checkboxElement.getBoundingClientRect();
	const centerX = rect.left + rect.width / 2;
	const centerY = rect.top + rect.height / 2;

	const particles: Particle[] = [];

	// Create burst particles
	for (let i = 0; i < 8; i++) {
		const angle = (i / 8) * Math.PI * 2 + (Math.random() - 0.5) * 0.8; // More random direction
		const speed = Math.random() * 2 + 1; // Slower initial speed
		particles.push({
			x: centerX,
			y: centerY,
			vx: Math.cos(angle) * speed,
			vy: Math.sin(angle) * speed,
			life: 0,
			maxLife: Math.random() * 30 + 25, // Longer lifespan
		});
	}

	const animate = () => {
		ctx.clearRect(0, 0, canvas.width, canvas.height);

		for (let i = particles.length - 1; i >= 0; i--) {
			const particle = particles[i];
			particle.life++;
			particle.x += particle.vx;
			particle.y += particle.vy;
			particle.vx *= 0.95; // More friction
			particle.vy *= 0.95;
			particle.vy += 0.08; // Gravity

			const alpha = 1 - particle.life / particle.maxLife;
			if (alpha <= 0) {
				particles.splice(i, 1);
				continue;
			}

			ctx.save();
			ctx.globalAlpha = alpha;
			ctx.fillStyle = '#805AD5';
			ctx.shadowColor = '#805AD5';
			ctx.shadowBlur = 6;
			ctx.beginPath();
			ctx.arc(particle.x, particle.y, 1.5, 0, Math.PI * 2);
			ctx.fill();
			ctx.restore();
		}

		if (particles.length > 0) {
			requestAnimationFrame(animate);
		} else {
			document.body.removeChild(canvas);
		}
	};

	animate();
};

export const DimensionCheckbox: FC<{ isChecked: boolean }> = ({ isChecked }) => {
	const checkboxRef = useRef<HTMLDivElement>(null);
	const prevChecked = useRef(isChecked);
	const { stage } = useContext(DimensionContext);

	if (!prevChecked.current && isChecked && checkboxRef.current && stage === 'checklist') {
		createCheckboxBurst(checkboxRef.current);
	}

	prevChecked.current = isChecked;

	return (
		<Center
			ref={checkboxRef}
			borderRadius="full"
			boxSize={rem(20)}
			border="1px solid"
			borderColor={isChecked ? 'purple.500' : 'border.default'}
			bgColor={isChecked ? 'purple.500' : 'bg.surface'}
			transform={isChecked ? 'scale(1.1)' : 'scale(1)'}
			transition="all 0.2s cubic-bezier(0.68, -0.55, 0.265, 1.55)"
		>
			{isChecked && <CheckSolid color="white" size="xs" />}
		</Center>
	);
};

export const DimensionExamples: FC<{ dimension: Dimension; text: string }> = ({ dimension, text }) => {
	const { data = { suggestions: [] }, isFetching } = useQualitySuggestions({ dimensionKey: dimension.key, text });

	return (
		<>
			{data.suggestions.map((suggestionText, index) => (
				<Box key={`${dimension.key}:${index}`} w="full" bgColor="blueAlpha.100" border="1px solid" borderColor="blueAlpha.200" borderRadius="sm" p={2}>
					<Text size="sm" color="text.default">
						&ldquo;{suggestionText}&rdquo;
					</Text>
				</Box>
			))}

			<Collapse in={isFetching}>
				<Center py={2}>
					<Spinner color="purple.300" size="sm" role="status" />
				</Center>
			</Collapse>
		</>
	);
};

export const CompanyValues = () => {
	const company = useCurrentCompany();

	return (
		<>
			{company.core_values.map((value) => (
				<Box key={value.hashtag} bgColor="blueAlpha.100" border="1px solid" borderColor="blueAlpha.200" borderRadius="sm" p={2}>
					<Heading color="primary.fgStrong">{value.hashtag}</Heading>
					<Reveal if={value.description.trim()}>
						<Text size="sm" mt={2} whiteSpace="pre-wrap" color="text.default">
							{value.description}
						</Text>
					</Reveal>
				</Box>
			))}
		</>
	);
};

type ScoreBoxAccordionContentProps = {
	dimensions: Dimension[];
	isLoading: boolean;
	text: string;
	draftAssist?: DraftAssistProp;
	showCupFiller?: boolean;
	/** When false, omits the "See examples" / "See company values" links from each dimension. Default: true. */
	showExamples?: boolean;
	onDimensionExpanded?: (key: string | null) => void;
};

export const ScoreBoxAccordionContent: FC<ScoreBoxAccordionContentProps> = ({
	dimensions,
	isLoading,
	text,
	draftAssist,
	showCupFiller = true,
	showExamples = true,
	onDimensionExpanded,
}) => {
	const state = useScoreboxState();
	const { t } = useTranslation('recognition', { keyPrefix: 'gratitudeGauge' });
	const matches = dimensions.filter((d) => d.matches).length;
	const percentage = calculatePercentageStop(matches, dimensions.length);
	const hasCoreValues = useCurrentCompany().core_values.length > 0;
	const derivedIndex = draftAssist?.dimensionKey != null ? dimensions.findIndex((d) => d.key === draftAssist.dimensionKey) : null;
	const overrideIndex = derivedIndex !== -1 ? derivedIndex : null;
	const { register } = useDimensionAccordion(overrideIndex);

	return (
		<DimensionContext.Provider value={{ stage: state.stage }}>
			<Box overflow="hidden" columnGap={4}>
				<Flex
					transform={state.isViewing ? `translateX(calc(-100% - ${rem(16)}))` : 'translateX(0)'}
					transition="transform 0.3s cubic-bezier(0.68, -0.3, 0.265, 1.25)"
					columnGap={4}
					w="full"
				>
					<Box w="full" flexShrink={0}>
						<Reveal if={!state.isViewing || state.isTransitioning}>
							{showCupFiller && (
								<HStack mb={4} columnGap={2}>
									<CupFiller flexShrink={0} percentage={percentage} />
									<SlidingText text={t('fillText', { context: percentage })} />
									<Fade in={isLoading}>
										<Center>
											<Spinner size="sm" color="purple.300" />
										</Center>
									</Fade>
								</HStack>
							)}

							<Stack rowGap={2}>
								{dimensions.map((dimension, index) => {
									const registered = register(index);
									const isMatchingDimension = draftAssist?.dimensionKey === dimension.key;
									const isLoadingForDimension =
										!!draftAssist?.isLoading && (draftAssist.loadingDimensionKey === dimension.key || draftAssist.dimensionKey === dimension.key);
									return (
										<DimensionBox
											key={dimension.key}
											dimension={dimension}
											onClick={showExamples ? () => state.onViewDimension(dimension) : undefined}
											text={text}
											isOpen={registered.isOpen}
											onToggle={() => {
												const nextIsOpen = !registered.isOpen;
												registered.onToggle();
												onDimensionExpanded?.(nextIsOpen ? dimension.key : null);
											}}
											isLoadingSuggestion={isLoadingForDimension}
											suggestion={isMatchingDimension ? draftAssist.suggestion : undefined}
											onAddSuggestion={isMatchingDimension && draftAssist?.onAdd ? (text: string) => draftAssist.onAdd?.(text) : undefined}
											onRegenerateSuggestion={isMatchingDimension ? draftAssist?.onRegenerate : undefined}
										/>
									);
								})}
							</Stack>
						</Reveal>
					</Box>

					<Box w="full" flexShrink={0}>
						<Reveal if={state.isViewing || state.isTransitioning}>
							<HStack columnGap={1} mb={4}>
								<Box as="button" px={2} onClick={state.onCloseDimension}>
									<ArrowLeftOutline size="xs" />
								</Box>
								<Heading color="text.default">{t('exampleTitle', { context: state.dimension?.key })}</Heading>
							</HStack>

							<Stack rowGap={2}>
								{state.dimension && (state.dimension.key !== DimensionKey.ValuesAligned || !hasCoreValues) && (
									<DimensionExamples dimension={state.dimension} text={text} />
								)}
								{state.dimension && state.dimension.key === DimensionKey.ValuesAligned && hasCoreValues && <CompanyValues />}

								<Button colorScheme="secondary" variant="alpha" mt={4} onClick={state.onCloseDimension}>
									{t('back')}
								</Button>
							</Stack>
						</Reveal>
					</Box>
				</Flex>

				<ScoreboxFeedback dimensions={dimensions} py={4} text={text} />
			</Box>
		</DimensionContext.Provider>
	);
};

type UseGratitudeFeedbackValue = {
	forceHide: boolean;
	reset: () => void;
	onFeedbackComplete: () => void;
};

export const useGratitudeFeedback = create<UseGratitudeFeedbackValue>((set) => ({
	forceHide: false,
	reset: () => set({ forceHide: false }),
	onFeedbackComplete: () => set({ forceHide: true }),
}));

type ScoreboxFeedbackProps = StyleProps & {
	text: string;
	dimensions: Dimension[];
};

export const ScoreboxFeedback: FC<ScoreboxFeedbackProps> = ({ dimensions, text, ...rest }) => {
	const { t } = useTranslation('recognition', { keyPrefix: 'gratitudeGauge' });
	const { track } = useAnalytics();
	const forceHide = useGratitudeFeedback((state) => state.forceHide);
	const onFeedbackComplete = useGratitudeFeedback((state) => state.onFeedbackComplete);

	const isVisible = !!text && !forceHide;

	const handleSelectFeedback = (type: string) => {
		const getMatch = (key: DimensionKey) => dimensions.find((dimension) => dimension.key === key)?.matches ?? false;

		// Manually adding properties to match Amplitude style guidelines and
		// keep data top level so it can be queried
		track(AnalyticsEvent.GratitudeGauge.FeedbackProvided, {
			matchesContextual: getMatch(DimensionKey.Contextual),
			matchesGenuine: getMatch(DimensionKey.Genuine),
			matchesPersonal: getMatch(DimensionKey.Personal),
			matchesSpecific: getMatch(DimensionKey.Specific),
			matchesValuesAligned: getMatch(DimensionKey.ValuesAligned),
			text,
			type,
		});

		onFeedbackComplete();
	};

	return (
		<Collapse in={isVisible}>
			<Box {...rest}>
				<Box bgColor="bg.canvas" borderRadius="md" p={2}>
					<Text textAlign="center" size="sm" color="text.softer" mb={2}>
						{t('feedbackTitle')}
					</Text>

					<Flex justifyContent="center" columnGap={2}>
						<IconButton
							aria-label={t('feedbackLabelNegative')}
							bgColor="bg.surface"
							color="purple.500"
							_hover={{
								bgColor: 'bg.soft',
							}}
							isRound
							onClick={() => handleSelectFeedback('negative')}
						>
							<ThumbDownOutline />
						</IconButton>

						<IconButton
							aria-label={t('feedbackLabelPositive')}
							bgColor="bg.surface"
							color="purple.500"
							_hover={{
								bgColor: 'bg.soft',
							}}
							isRound
							onClick={() => handleSelectFeedback('positive')}
						>
							<ThumbUpOutline />
						</IconButton>
					</Flex>
				</Box>
			</Box>
		</Collapse>
	);
};
